import { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { Pane } from "components/ui"
import { Box, Button } from "@planningcenter/tapestry-react"
import TextEditor from "components/admin/TextEditor"

const PanelTextEditor = ({
  name,
  heading,
  blankStateMessage,
  blankStateButtonLabel = "Edit message",
  suppressBlankStateButton = false,
  editButtonLabel = "Edit",
  onSave,
  onChange = () => {},
  contentHtml = "",
  saveStatus = null,
  paneTitleClass,
  onClose = () => {}
}) => {
  const richTextEditorRef = useRef()

  const [status, setStatus] = useState(saveStatus)
  const [editing, setEditing] = useState(false)
  const [content, setContent] = useState(contentHtml)

  useEffect(() => {
    setContent(contentHtml)
  }, [contentHtml])

  useEffect(() => {
    setStatus(saveStatus)
  }, [saveStatus])

  const handleContentChange = e => {
    setContent(e.target.value)
    onChange(e.target.value)
  }

  const handleEdit = () => setEditing(true)

  const handleClose = () => {
    setEditing(false)
    setStatus(null)
    onClose()
  }

  const handleSave = async () => {
    setStatus({ text: "Saving...", icon: "spinner" })
    await richTextEditorRef.current.handleAttachments()

    onSave(content, handleClose)
  }

  return (
    <Pane>
      <Pane.Header>
        <Pane.Title className={paneTitleClass}>{heading}</Pane.Title>
        {!editing && (
          <Button title={editButtonLabel} size="xs" variant="outline" onClick={handleEdit} />
        )}
        {editing && (
          <div>
            <Button title="Cancel" size="xs" variant="outline" onClick={handleClose} />
            <Button title="Save" theme="create" size="xs" onClick={handleSave} marginLeft={1} />
          </div>
        )}
      </Pane.Header>

      <Pane.Body>
        {editing && (
          <div css={{ marginTop: -12 }}>
            <TextEditor
              richTextEditorRef={richTextEditorRef}
              name={name}
              onChange={handleContentChange}
              value={content}
              saveStatus={status}
            />
          </div>
        )}
        {!editing && contentHtml && (
          <div
            className="markdown-content"
            dangerouslySetInnerHTML={{
              __html: contentHtml
            }}
          />
        )}
        {!editing && !contentHtml && (
          <Pane.BlankState>
            <Box>{blankStateMessage}</Box>
            {!suppressBlankStateButton && (
              <Button
                marginTop={1}
                title={blankStateButtonLabel}
                size="sm"
                theme="app"
                onClick={handleEdit}
              />
            )}
          </Pane.BlankState>
        )}
      </Pane.Body>
    </Pane>
  )
}

PanelTextEditor.propTypes = {
  name: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  heading: PropTypes.string.isRequired,
  blankStateMessage: PropTypes.string,
  blankStateButtonLabel: PropTypes.string,
  suppressBlankStateButton: PropTypes.bool,
  editButtonLabel: PropTypes.string,
  contentHtml: PropTypes.string,
  saveStatus: PropTypes.shape({}),
  paneTitleClass: PropTypes.string,
  onChange: PropTypes.func,
  onClose: PropTypes.func
}

export default PanelTextEditor
